import { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Box, Button, Link, Paper, Stack, TextField, Typography } from '@mui/material';
import { authApi } from '../../shared/api/auth';
import { AuthLayout } from '../components/AuthLayout';

const schema = z
  .object({
    newPassword: z.string().min(8, 'Use at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords must match',
  });

type FormValues = z.infer<typeof schema>;

export default function ResetPasswordPage(): JSX.Element {
  const [params] = useSearchParams();
  const email = params.get('email') ?? '';
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const tokenMissing = !email || !token;

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      await authApi.resetPassword(email, token, values.newPassword);
      navigate('/login', {
        replace: true,
        state: { resetSuccess: true },
      });
    } catch {
      setServerError('This reset link is invalid or has expired. Request a new one.');
    }
  });

  return (
    <AuthLayout title="Reset password">
      <Paper sx={{ p: 4, width: '100%', maxWidth: 400 }} elevation={2}>
        <Typography variant="h5" gutterBottom>
          Choose a new password
        </Typography>
        {tokenMissing ? (
          <Stack spacing={2}>
            <Alert severity="error">This reset link is missing required information.</Alert>
            <Link component={RouterLink} to="/forgot-password">
              Request a new reset link
            </Link>
          </Stack>
        ) : (
          <Box component="form" onSubmit={onSubmit} noValidate>
            <Stack spacing={2}>
              {serverError && <Alert severity="error">{serverError}</Alert>}
              <TextField
                label="New password"
                type="password"
                autoComplete="new-password"
                error={Boolean(errors.newPassword)}
                helperText={errors.newPassword?.message}
                {...register('newPassword')}
              />
              <TextField
                label="Confirm password"
                type="password"
                autoComplete="new-password"
                error={Boolean(errors.confirmPassword)}
                helperText={errors.confirmPassword?.message}
                {...register('confirmPassword')}
              />
              <Button type="submit" variant="contained" disabled={isSubmitting} size="large">
                {isSubmitting ? 'Saving…' : 'Save new password'}
              </Button>
            </Stack>
          </Box>
        )}
      </Paper>
    </AuthLayout>
  );
}
